


import React, { useState } from 'react';
import { MIN_TIER, MAX_TIER } from '../config';
import InfoModal from './InfoModal';

interface TierControlCardProps {
    tier: number;
    maxLimit: number;
    onTierChange: (tier: number) => void;
    onResetAll: () => void;
    isConfirmationEnabled: boolean;
    onConfirmationToggle: () => void;
    isDarkMode: boolean;
    onThemeToggle: () => void;
    onOpenSortModal: () => void;
}

const TierControlCard: React.FC<TierControlCardProps> = ({ tier, maxLimit, onTierChange, onResetAll, isConfirmationEnabled, onConfirmationToggle, isDarkMode, onThemeToggle, onOpenSortModal }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isOpen, setIsOpen] = useState(false); // Default to collapsed

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value === '' ? MIN_TIER : parseInt(e.target.value, 10);
        onTierChange(value);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        const value = parseInt(e.target.value, 10);
        if (isNaN(value) || value < MIN_TIER) {
            onTierChange(MIN_TIER);
        } else if (value > MAX_TIER) {
            onTierChange(MAX_TIER);
        }
    };
    
    const handleIncrement = () => {
        if (tier < MAX_TIER) onTierChange(tier + 1);
    };

    const handleDecrement = () => {
        if (tier > MIN_TIER) onTierChange(tier - 1);
    };

    const TierControls = () => (
        <>
            <div className="h-6 w-px bg-gray-200 dark:bg-gray-700 hidden sm:block"></div>
            <label htmlFor="tier-input" className="text-sm font-medium text-gray-600 dark:text-gray-400">
                TIER
            </label>
            <div className="flex items-center bg-gray-100 dark:bg-slate-900/50 rounded-lg p-0.5 shadow-inner">
                <button type="button" onClick={handleDecrement} 
                        className="w-8 h-8 flex items-center justify-center text-gray-800 dark:text-gray-200 rounded-md hover:bg-gray-200 dark:hover:bg-slate-700/80 transition duration-150 text-xl font-bold p-1 leading-none active:scale-95">
                        &minus;
                </button>
                <input 
                    type="number" 
                    id="tier-input" 
                    value={tier} 
                    min={MIN_TIER} 
                    max={MAX_TIER}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    className="w-12 p-0 bg-transparent text-indigo-600 dark:text-indigo-300 border-none focus:ring-0 text-lg font-bold text-center appearance-none"
                />
                <button type="button" onClick={handleIncrement} 
                        className="w-8 h-8 flex items-center justify-center text-gray-800 dark:text-gray-200 rounded-md hover:bg-gray-200 dark:hover:bg-slate-700/80 transition duration-150 text-xl font-bold p-1 leading-none active:scale-95">
                        +
                </button>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 ml-1">上限: <span className="font-bold text-indigo-600 dark:text-indigo-400">{maxLimit} pt</span></p>
        </>
    );

    return (
        <>
            <div className="card bg-white dark:bg-slate-800 p-6 rounded-xl mb-8 border-t-4 border-indigo-500 transition-colors duration-300">
                <div className="flex items-center justify-between gap-x-4">
                    {/* Left: Title + Toggle Icon */}
                    <button
                        type="button"
                        onClick={() => setIsOpen(!isOpen)}
                        aria-expanded={isOpen}
                        aria-controls="global-settings-content"
                        className="flex items-center gap-2 text-left flex-shrink-0"
                    >
                        <span className="text-xl font-bold text-gray-800 dark:text-gray-200 whitespace-nowrap">全局設定</span>
                        <svg xmlns="http://www.w3.org/2000/svg" className={`h-5 w-5 text-gray-500 dark:text-gray-400 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor">
                           <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                    </button>

                    {/* Right: Desktop TIER + Buttons */}
                    <div className="flex items-center gap-2 sm:gap-x-4 lg:gap-x-6">
                        {/* Desktop-only TIER Controls */}
                        <div className="hidden sm:flex items-center gap-x-3">
                            <TierControls />
                        </div>

                        {/* Action Buttons */}
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                onClick={() => setIsModalOpen(true)}
                                className="flex items-center justify-center h-12 w-12 rounded-lg bg-indigo-500 dark:bg-indigo-600/80 text-white text-sm font-semibold hover:bg-indigo-600 dark:hover:bg-indigo-600 transition duration-150 active:scale-95 flex-shrink-0 shadow lg:w-auto lg:px-4 lg:py-2"
                                aria-label="顯示計分說明"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                </svg>
                                <span className="hidden lg:inline ml-2">說明</span>
                            </button>
                            <button
                                type="button"
                                onClick={onResetAll}
                                className="flex items-center justify-center h-12 w-12 rounded-lg bg-red-500 dark:bg-red-600/80 text-white text-sm font-semibold hover:bg-red-600 dark:hover:bg-red-600 transition duration-150 active:scale-95 flex-shrink-0 shadow lg:w-auto lg:px-4 lg:py-2"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M18.4 10.6C16.55 8.99 14.15 8 11.5 8c-4.65 0-8.58 3.03-9.96 7.22L3.9 16c1.05-3.19 4.05-5.5 7.6-5.5 1.95 0 3.73.72 5.12 1.88L13 16h9V7l-3.6 3.6z"/>
                                </svg>
                                 <span className="hidden lg:inline ml-2">全部重置</span>
                            </button>
                        </div>
                    </div>
                </div>
                
                <div
                    id="global-settings-content"
                    className={`transition-all duration-500 ease-in-out overflow-hidden ${isOpen ? 'max-h-[1000px] pt-4 mt-4 border-t border-gray-200 dark:border-gray-700' : 'max-h-0'}`}
                >
                    {/* Mobile-only TIER Controls */}
                    <div className="sm:hidden mb-4 pb-4 border-b border-gray-200 dark:border-gray-700">
                        <div className="flex items-center justify-center gap-x-3 flex-wrap">
                            <TierControls />
                        </div>
                    </div>

                    <div className="space-y-4">
                        <div className="flex justify-between items-center">
                            <div className="flex flex-col">
                                <label htmlFor="confirm-toggle" className="font-medium text-gray-700 dark:text-gray-300">
                                    重置前確認
                                </label>
                                <p className="text-xs text-gray-500 dark:text-gray-400">啟用後，執行重置操作前會彈出確認視窗。</p>
                            </div>
                            <div className="relative inline-block w-10 align-middle select-none transition duration-200 ease-in">
                                <input
                                    type="checkbox"
                                    name="confirm-toggle"
                                    id="confirm-toggle"
                                    checked={isConfirmationEnabled}
                                    onChange={onConfirmationToggle}
                                    className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
                                />
                                <label htmlFor="confirm-toggle" className="toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 dark:bg-slate-600 cursor-pointer"></label>
                            </div>
                        </div>
                        <div className="flex justify-between items-center">
                            <div className="flex flex-col">
                                <label htmlFor="theme-toggle" className="font-medium text-gray-700 dark:text-gray-300">
                                    深色模式
                                </label>
                                <p className="text-xs text-gray-500 dark:text-gray-400">切換至適合低光源環境的介面主題。</p>
                            </div>
                            <div className="relative inline-block w-10 align-middle select-none transition duration-200 ease-in">
                                <input
                                    type="checkbox"
                                    name="theme-toggle"
                                    id="theme-toggle"
                                    checked={isDarkMode}
                                    onChange={onThemeToggle}
                                    className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
                                />
                                <label htmlFor="theme-toggle" className="toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 dark:bg-slate-600 cursor-pointer"></label>
                            </div>
                        </div>
                        <div className="flex justify-between items-center">
                            <div className="flex flex-col">
                                <label className="font-medium text-gray-700 dark:text-gray-300">
                                    按鈕排序
                                </label>
                                <p className="text-xs text-gray-500 dark:text-gray-400">自訂角色卡片上的操作按鈕順序。</p>
                            </div>
                            <button
                                type="button"
                                onClick={onOpenSortModal}
                                className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 dark:bg-slate-700/50 dark:text-indigo-300 dark:border-slate-600 dark:hover:bg-slate-600/50 transition duration-150 active:scale-95 shadow-sm"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z" clipRule="evenodd" />
                                </svg>
                                <span>設定排序</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {isModalOpen && <InfoModal onClose={() => setIsModalOpen(false)} />}
        </>
    );
};

export default TierControlCard;